import React, { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { fmtCurrency } from "@/lib/api";
import { X, Printer } from "lucide-react";

/**
 * A4 sheet, 3 columns × 10 rows = 30 labels per page.
 * Each label ~68mm × 27mm — sized for the common Avery-style label sheets
 * used in Indian retail. Content: part_number + Code-128 barcode + short
 * name + MRP.
 */
function Barcode({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: "CODE128",
        displayValue: false,
        margin: 0,
        height: 28,
        width: 1.4,
        background: "#ffffff",
        lineColor: "#000000",
      });
    } catch (e) {
      /* invalid part number for barcode — silently skip */
    }
  }, [value]);
  return <svg ref={ref} className="w-full h-8" />;
}

export default function LabelPrintModal({ parts, onClose }) {
  // per-part copy count; default 1
  const [copies, setCopies] = useState(() =>
    Object.fromEntries(parts.map((p) => [p.id, 1])),
  );

  const totalLabels = Object.values(copies).reduce((a, b) => a + (Number(b) || 0), 0);
  const totalSheets = Math.ceil(totalLabels / 30);

  // Expand parts×copies into a flat array of label instances
  const labels = parts.flatMap((p) => Array(Math.max(0, Number(copies[p.id]) || 0)).fill(p));

  const doPrint = () => {
    document.body.classList.add("label-print");
    const cleanup = () => {
      document.body.classList.remove("label-print");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 50);
  };

  return (
    <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-white border border-zinc-300 w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="label-modal">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Print</div>
            <h3 className="text-xl font-semibold text-zinc-950">Barcode Labels (A4 · 3×10)</h3>
            <p className="text-xs font-mono text-zinc-500 mt-1 uppercase tracking-wider">
              {parts.length} parts · {totalLabels} labels · {totalSheets} sheet{totalSheets !== 1 ? "s" : ""}
            </p>
          </div>
          <button onClick={onClose} data-testid="label-close-button" className="p-1.5 border border-zinc-300 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <table className="w-full text-sm border border-zinc-200">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
                <th className="text-left px-4 py-2 font-semibold">Part #</th>
                <th className="text-left px-4 py-2 font-semibold">Name</th>
                <th className="text-right px-4 py-2 font-semibold">MRP</th>
                <th className="text-right px-4 py-2 font-semibold">Copies</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id} className="border-t border-zinc-200">
                  <td className="px-4 py-2 font-mono text-zinc-950">{p.part_number}</td>
                  <td className="px-4 py-2 text-zinc-700">{p.name || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtCurrency(p.mrp)}</td>
                  <td className="px-4 py-2 text-right">
                    <input
                      data-testid={`label-copies-${p.part_number}`}
                      type="number"
                      min={0}
                      max={999}
                      value={copies[p.id]}
                      onChange={(e) => setCopies({ ...copies, [p.id]: e.target.value })}
                      className="w-20 border border-zinc-300 px-2 py-1 text-right font-mono text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-zinc-300 text-sm uppercase tracking-wider hover:bg-zinc-50">
            Cancel
          </button>
          <button
            data-testid="label-print-button"
            onClick={doPrint}
            disabled={totalLabels === 0}
            className="px-4 py-2 bg-zinc-950 text-white text-sm uppercase tracking-wider hover:bg-zinc-800 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Printer className="w-3.5 h-3.5" /> Print {totalLabels} labels
          </button>
        </div>
      </div>

      {/* Print-only sheet — the CSS in index.css shows this only when body has .label-print */}
      <div className="label-sheet" data-testid="label-sheet">
        {labels.map((p, idx) => (
          <div className="label-cell" key={`${p.id}-${idx}`}>
            <div className="label-partno">{p.part_number}</div>
            <Barcode value={p.part_number} />
            {p.name && <div className="label-name">{p.name}</div>}
            <div className="label-price">{fmtCurrency(p.mrp)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
